import React, { useEffect, useState } from "react";
import { Button, Skeleton, Spin } from "antd";
import {
  FileTextOutlined,
  CheckCircleOutlined,
  SmileOutlined,
  DislikeOutlined,
  FrownOutlined,
  TeamOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

const BRAND_ORANGE = "#F7941D";
const SLOW_MS = 3000;

const cache = new Map();

const rowKey = (recordOrId) =>
  typeof recordOrId === "object"
    ? recordOrId?.game_id ?? recordOrId?.id
    : recordOrId;

/** Map indicator-max API result → Quick View metrics. Pure. */
export const mapIndicatorMaxToQuickView = (result) => {
  const r = Array.isArray(result) ? result[0] : result;
  const total = Number(r?.total_grades) || 0;
  const executed = Number(r?.total_assignment_executed) || 0;
  const effort = Number(r?.total_great_effort) || 0;
  const read = Number(r?.total_marked_as_read) || 0;

  return {
    assignmentsGraded: total,
    executionScore: total ? Math.round((executed / total) * 100) : 0,
    effortScore: total ? Math.round((effort / total) * 100) : 0,
    missedAssignments: Math.max(0, total - executed),
    poorEffortPlays: Math.max(0, total - effort),
    reviewsRead: read,
    reviewsTotal: total,
  };
};

export const EMPTY_QUICK_VIEW = mapIndicatorMaxToQuickView(null);

/** Wrap Redux gamesIndicatorMax into Promise<metrics>. Pure factory. */
export const createQuickViewFetcher = (gamesIndicatorMax) => (gameId) =>
  new Promise((resolve, reject) => {
    if (!gamesIndicatorMax) {
      reject(new Error("gamesIndicatorMax unavailable"));
      return;
    }
    gamesIndicatorMax({
      params: { games: String(gameId) },
      callback: (ok, data) => {
        if (ok === true) {
          resolve(mapIndicatorMaxToQuickView(data?.data?.result));
        } else {
          reject(new Error("Failed to load game performance"));
        }
      },
    });
  });

const METRICS = [
  {
    key: "assignmentsGraded",
    label: "Assignments Graded",
    icon: FileTextOutlined,
    color: "#1A91FF",
    format: (d) => d.assignmentsGraded,
  },
  {
    key: "executionScore",
    label: "Execution Score",
    icon: CheckCircleOutlined,
    color: "#12CF52",
    format: (d) => `${d.executionScore}%`,
  },
  {
    key: "effortScore",
    label: "Effort Score",
    icon: SmileOutlined,
    color: BRAND_ORANGE,
    format: (d) => `${d.effortScore}%`,
  },
  {
    key: "missedAssignments",
    label: "Missed Assignments",
    icon: DislikeOutlined,
    color: "#E74C3C",
    format: (d) => d.missedAssignments,
  },
  {
    key: "poorEffortPlays",
    label: "Poor Effort Plays",
    icon: FrownOutlined,
    color: "#A40E17",
    format: (d) => d.poorEffortPlays,
  },
];

const MetricCard = ({ icon: Icon, color, value, label }) => (
  <div className="game-perf-metric-card">
    <span
      className="game-perf-metric-icon"
      style={{ backgroundColor: `${color}1f`, color }}
    >
      <Icon style={{ fontSize: 18 }} />
    </span>
    <div className="game-perf-metric-value">{value}</div>
    <div className="game-perf-metric-label">{label}</div>
  </div>
);

const RefreshButton = ({ onRefresh, refreshing, disabled }) => (
  <Button
    type="text"
    size="small"
    className="game-perf-refresh-btn"
    icon={<ReloadOutlined spin={refreshing} />}
    onClick={onRefresh}
    disabled={disabled || refreshing}
    aria-label="Refresh game performance"
  >
    Refresh
  </Button>
);

const QuickViewSkeleton = () => (
  <div className="game-perf-quick-view">
    <Skeleton
      active
      title={{ width: 160 }}
      paragraph={false}
      className="game-perf-skel-title"
    />
    <div className="game-perf-metrics">
      {METRICS.map(({ key }) => (
        <div key={key} className="game-perf-metric-card game-perf-metric-skel">
          <Skeleton.Avatar active size={36} shape="circle" />
          <Skeleton.Input active size="small" style={{ width: 56, height: 28 }} />
          <Skeleton.Input active size="small" style={{ width: 88, height: 14 }} />
        </div>
      ))}
    </div>
    <Skeleton.Input active block style={{ height: 42, borderRadius: 4 }} />
  </div>
);

const QuickViewSlowLoading = () => (
  <div className="game-perf-quick-view game-perf-loading-slow">
    <Spin size="large" />
    <p>
      This is taking unusually long, please wait while we fetch your data
    </p>
  </div>
);

const QuickViewFail = ({ onRefresh, refreshing }) => (
  <div className="game-perf-quick-view game-perf-fail">
    <div className="game-perf-fail-title">Oops</div>
    <p>Something went wrong. Please refresh and try again.</p>
    {onRefresh && (
      <RefreshButton onRefresh={onRefresh} refreshing={refreshing} />
    )}
  </div>
);

export const GamePerformanceQuickView = ({
  data,
  onViewAnalytics,
  onRefresh,
  refreshing,
}) => {
  if (!data) return null;

  const readPct =
    data.reviewsTotal > 0
      ? Math.round((data.reviewsRead / data.reviewsTotal) * 100)
      : 0;

  return (
    <div className="game-perf-quick-view">
      <div className="game-perf-header">
        <div className="game-perf-title">Game Performance</div>
        {onRefresh && (
          <RefreshButton onRefresh={onRefresh} refreshing={refreshing} />
        )}
      </div>

      <div className="game-perf-metrics">
        {METRICS.map(({ key, icon, color, label, format }) => (
          <MetricCard
            key={key}
            icon={icon}
            color={color}
            value={format(data)}
            label={label}
          />
        ))}
      </div>

      <div className="game-perf-footer">
        <div className="game-perf-review">
          <TeamOutlined style={{ color: "#1A91FF", fontSize: 16 }} />
          <span>
            <strong>PLAYER REVIEW:</strong> {data.reviewsRead} of{" "}
            {data.reviewsTotal} ({readPct}%) Assignments Marked As Read
          </span>
        </div>
        <Button
          className="game-perf-analytics-btn"
          onClick={onViewAnalytics}
        >
          View Game Analytics →
        </Button>
      </div>
    </div>
  );
};

/**
 * Parent Table unmounts this on collapse; remount always refetches.
 * Cache only paints stale UI while the network request runs (SWR).
 * Manual refresh uses the same load path without wiping visible data.
 */
export const GamePerformanceExpandPanel = ({
  record,
  onViewAnalytics,
  fetchMetrics,
}) => {
  const key = rowKey(record);
  const [data, setData] = useState(() => cache.get(key) ?? null);
  const [loading, setLoading] = useState(() => !cache.has(key));
  const [refreshing, setRefreshing] = useState(false);
  const [slow, setSlow] = useState(false);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const cached = cache.get(key);
    const soft = Boolean(cached);

    if (cached) setData(cached);

    setLoading(!soft);
    setRefreshing(soft);
    setSlow(false);
    setError(false);

    const slowTimer = setTimeout(() => {
      if (!cancelled) setSlow(true);
    }, SLOW_MS);

    const run = fetchMetrics
      ? fetchMetrics(key)
      : Promise.reject(new Error("No fetcher"));

    run
      .then((result) => {
        if (cancelled) return;
        cache.set(key, result);
        setData(result);
        setLoading(false);
        setRefreshing(false);
        setSlow(false);
        setError(false);
      })
      .catch(() => {
        if (cancelled) return;
        const keep = cache.has(key);
        if (!keep) setData(null);
        setError(!keep);
        setLoading(false);
        setRefreshing(false);
        setSlow(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(slowTimer);
    };
    // reloadToken: manual refresh; key: remount/reopen for a different game
    // fetchMetrics identity may change each parent render — intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, reloadToken]);

  const handleRefresh = () => setReloadToken((n) => n + 1);

  if (loading) return slow ? <QuickViewSlowLoading /> : <QuickViewSkeleton />;
  if (error) {
    return (
      <QuickViewFail onRefresh={handleRefresh} refreshing={refreshing} />
    );
  }

  return (
    <GamePerformanceQuickView
      data={data}
      onViewAnalytics={onViewAnalytics}
      onRefresh={handleRefresh}
      refreshing={refreshing}
    />
  );
};

export default GamePerformanceExpandPanel;
